import axios from 'axios';
import { getAccessToken } from '../lib/supabase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
});

// Add request interceptor for authentication
api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};

  // Include Supabase token if available
  const token = await getAccessToken();
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }

  // Include x-user-id only when explicitly allowed (staging/dev)
  const allowHeaderUserId = process.env.REACT_APP_ALLOW_HEADER_USER_ID === '1' && process.env.NODE_ENV !== 'production';
  if (allowHeaderUserId) {
    const userId = localStorage.getItem('userId');
    if (userId) {
      (config.headers as any)['x-user-id'] = userId;
    }
  }

  return config;
});

// Allow App to listen for authorization issues to surface a banner
export type AuthIssue = {
  status: number;
  url?: string;
  hadAuthHeader: boolean;
  hadUserIdHeader: boolean;
  blockedUntil?: string;
};

let authIssueHandler: ((issue: AuthIssue) => void) | null = null;
export const onAuthIssue = (handler: (issue: AuthIssue) => void) => {
  authIssueHandler = handler;
};

// Function to update the user ID for API calls
export const setApiUserId = (userId: string | null) => {
  if (userId) {
    localStorage.setItem('userId', userId);
  } else {
    localStorage.removeItem('userId');
  }
};

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const url: string | undefined = error.config?.url || error.response?.config?.url;
    // Suppress expected unauthenticated errors for auth/me
    if (url?.includes('/auth/me') && (status === 401 || status === 404)) {
      return Promise.reject(error);
    }
    if (status === 429) {
      
    } else {
      console.error('API Error:', error.response?.data || error.message);
    }

    // Notify app on auth issues if we actually sent auth headers
    if ((status === 401 || status === 403) && authIssueHandler) {
      const headers = (error.config?.headers || {}) as any;
      const hadAuth = Boolean(headers.Authorization || headers.authorization);
      const hadUserId = Boolean(headers['x-user-id'] || headers['X-User-Id'] || headers['x-User-Id']);
      if (hadAuth || hadUserId) {
        const blockedUntil = error.response?.data?.blockedUntil || error.response?.data?.bannedUntil;
        try { authIssueHandler({ status, url, hadAuthHeader: hadAuth, hadUserIdHeader: hadUserId, blockedUntil }); } catch {}
      }
    }
    return Promise.reject(error);
  }
);

export { API_BASE_URL };
